/**
 * BeCoAuditExportCard — Directive 12.
 *
 * Super-admin dashboard card that renders a preview of the BeCo
 * cryptographic audit chain and offers CSV + PDF export buttons.
 *
 * Filters:
 *   • since / until (ISO datetime, optional)
 *   • limit (default 100 preview, 100k CSV, 5k PDF per backend)
 *
 * Exports call the streaming backend routes and trigger a browser
 * download without going through JSON.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FileText, FileSpreadsheet, RefreshCw, ShieldCheck, Download } from "lucide-react";
import { Jargon } from "@/components/admin/JargonTerm";

function truncateHash(h) {
    if (!h || typeof h !== "string") return "—";
    return h.length > 14 ? h.slice(0, 10) + "…" : h;
}

export default function BeCoAuditExportCard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyCsv, setBusyCsv] = useState(false);
    const [busyPdf, setBusyPdf] = useState(false);
    const [since, setSince] = useState("");
    const [until, setUntil] = useState("");

    async function loadPreview() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("limit", "50");
            if (since) params.set("since", since);
            if (until) params.set("until", until);
            const { data } = await api.get(`/beco/audit/rows?${params.toString()}`);
            setRows(data.rows || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load audit preview");
        } finally { setLoading(false); }
    }

    async function download(kind) {
        const busy = kind === "csv" ? setBusyCsv : setBusyPdf;
        busy(true);
        try {
            const params = new URLSearchParams();
            if (since) params.set("since", since);
            if (until) params.set("until", until);
            const { data, headers } = await api.get(`/beco/audit/export.${kind}?${params.toString()}`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = (headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `beco_audit.${kind}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Downloaded ${kind.toUpperCase()} audit export`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || `Failed to export ${kind}`);
        } finally { busy(false); }
    }

    useEffect(() => { loadPreview(); /* eslint-disable-line */ }, []);

    return (
        <Card data-testid="beco-audit-export-card">
            <CardHeader className="flex-row items-center justify-between space-y-0">
                <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                        <Jargon term="audit_chain" /> export
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                        Directive 12 · Async CSV + PDF export of the SHA-256 tamper-proof audit log. Exports are meta-audited (who / when / what filter).
                    </p>
                </div>
                <div className="flex items-center gap-1.5">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => download("csv")}
                        disabled={busyCsv}
                        data-testid="beco-audit-export-csv"
                    >
                        <FileSpreadsheet className={`w-4 h-4 mr-1.5 ${busyCsv ? "animate-spin" : ""}`} />
                        {busyCsv ? "Exporting…" : "CSV"}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => download("pdf")}
                        disabled={busyPdf}
                        data-testid="beco-audit-export-pdf"
                    >
                        <FileText className={`w-4 h-4 mr-1.5 ${busyPdf ? "animate-spin" : ""}`} />
                        {busyPdf ? "Exporting…" : "PDF"}
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                    <Input
                        placeholder="Since (2026-01-01T00:00:00Z)"
                        value={since}
                        onChange={(e) => setSince(e.target.value)}
                        data-testid="beco-audit-since"
                    />
                    <Input
                        placeholder="Until (ISO)"
                        value={until}
                        onChange={(e) => setUntil(e.target.value)}
                        data-testid="beco-audit-until"
                    />
                    <Button
                        variant="outline"
                        onClick={loadPreview}
                        disabled={loading}
                        data-testid="beco-audit-refresh"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                        Refresh preview
                    </Button>
                </div>
                <div className="overflow-auto max-h-[520px]">
                    {loading && <div className="text-sm text-muted-foreground py-3">Loading audit preview…</div>}
                    {!loading && rows.length === 0 && (
                        <div className="text-sm text-muted-foreground py-3">
                            No audit rows in this window.
                        </div>
                    )}
                    {!loading && rows.length > 0 && (
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                                    <th className="py-2 pr-3">TS (UTC)</th>
                                    <th className="py-2 pr-3">Event</th>
                                    <th className="py-2 pr-3">User hash</th>
                                    <th className="py-2 pr-3">Chain tip</th>
                                    <th className="py-2 pr-3">Prev hash</th>
                                    <th className="py-2 pr-3"><Jargon term="latency" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, idx) => (
                                    <tr key={idx} className="border-b border-border/40" data-testid={`beco-audit-row-${idx}`}>
                                        <td className="py-1.5 pr-3 font-mono">{(r.ts || "").slice(0, 19).replace("T", " ")}</td>
                                        <td className="py-1.5 pr-3">
                                            <Badge variant="outline" className="text-[10px]">{r.event_type}</Badge>
                                        </td>
                                        <td className="py-1.5 pr-3 font-mono">{truncateHash(r.user_id_hash)}</td>
                                        <td className="py-1.5 pr-3 font-mono">{truncateHash(r.chain_hash)}</td>
                                        <td className="py-1.5 pr-3 font-mono text-muted-foreground">{truncateHash(r.prev_hash)}</td>
                                        <td className="py-1.5 pr-3 font-mono">{r.control_lat_ms ?? "—"}ms</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Download className="w-3 h-3" />
                    <span>Privacy-redacted export — internal salt + payload never leave the boundary.</span>
                </div>
            </CardContent>
        </Card>
    );
}
